import React from 'react';
import { Grid, Typography, Box, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { InfoCard } from '@backstage/core-components';
import ChatIcon from '@material-ui/icons/Chat';
import SettingsInputComponentIcon from '@material-ui/icons/SettingsInputComponent';
import CodeIcon from '@material-ui/icons/Code';
import CloudIcon from '@material-ui/icons/Cloud';
import ComputerIcon from '@material-ui/icons/Computer';

const useStyles = makeStyles(theme => ({
  toolItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  iconBox: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
  },
  icon: {
    color: theme.palette.common.white,
    fontSize: 24,
  },
  toolName: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  toolDescription: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  chip: {
    height: 20,
    fontSize: '0.65rem',
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
  },
}));

interface Tool {
  name: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  badge?: string;
  badgeColor?: 'primary' | 'secondary' | 'default';
}

const tools: Tool[] = [
  {
    name: 'AI Assistant',
    description: 'Chat with our AI helper',
    icon: <ChatIcon />,
    href: '/ai-assistant',
    badge: 'Built-in',
    badgeColor: 'primary',
  },
  {
    name: 'MCP Servers',
    description: 'Model Context Protocol tools',
    icon: <SettingsInputComponentIcon />,
    href: '/',
    badge: 'Cursor',
  },
  {
    name: 'Microsoft AI Foundry',
    description: 'Enterprise AI models',
    icon: <CloudIcon />,
    href: 'https://azure.microsoft.com/en-us/products/ai-foundry',
    badge: 'External',
  },
  {
    name: 'GitHub Copilot',
    description: 'AI pair programming',
    icon: <CodeIcon />,
    href: 'https://github.com/features/copilot',
    badge: 'External',
  },
  {
    name: 'Cursor',
    description: 'AI-powered code editor',
    icon: <ComputerIcon />,
    href: 'https://cursor.com',
    badge: 'External',
  },
];

export const AiToolsSection = () => {
  const classes = useStyles();

  return (
    <InfoCard title="AI Tools">
      <Grid container spacing={2}>
        {tools.map(tool => (
          <Grid item xs={6} key={tool.name}>
            <a href={tool.href} className={classes.link}>
              <Box className={classes.toolItem}>
                <Box className={classes.iconBox}>
                  {React.cloneElement(tool.icon as React.ReactElement, {
                    className: classes.icon,
                  })}
                </Box>
                <Typography variant="body2" className={classes.toolName}>
                  {tool.name}
                </Typography>
                <Typography className={classes.toolDescription}>
                  {tool.description}
                </Typography>
                {tool.badge && (
                  <Chip
                    label={tool.badge}
                    size="small"
                    color={tool.badgeColor || 'default'}
                    className={classes.chip}
                    variant="outlined"
                  />
                )}
              </Box>
            </a>
          </Grid>
        ))}
      </Grid>
    </InfoCard>
  );
};
